<template>
    <Container>
        <div class="my-4 flex w-full items-center justify-between">
            <h1 class="text-2xl font-bold text-white">Create Song</h1>
            <Link href="/backstage/songs"
                class="rounded-md bg-nomad-900 px-4 py-2 text-white transition-colors hover:bg-nomad-800">
                Cancel
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-5xl space-y-4">
            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Basic Information</h2>
                <div class="grid gap-4 md:grid-cols-2">
                    <div class="md:col-span-2">
                        <label for="song-name" class="mb-2 block text-sm font-medium text-nomad-300">
                            Song name <span class="text-red-400">*</span>
                        </label>
                        <input id="song-name" v-model="form.name" type="text"
                            class="w-full rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Enter song name" />
                    </div>
                    <div>
                        <label for="song-release-year" class="mb-2 block text-sm font-medium text-nomad-300">Release year</label>
                        <input id="song-release-year" v-model="form.released" type="number"
                            class="w-full rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Year" />
                    </div>
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Playlist</label>
                        <Selector type="playlists" placeholder="Select playlist" v-model="form.playlists" max="10" />
                    </div>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Artists</h2>
                <div class="grid gap-4 md:grid-cols-2">
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">
                            Main artist <span class="text-red-400">*</span>
                        </label>
                        <Selector type="artists" v-model="form.artist" placeholder="Select artist" />
                    </div>
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Original singer</label>
                        <Selector type="artists" v-model="form.original_artist" placeholder="Select original singer" />
                    </div>
                    <div class="md:col-span-2">
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Featuring artists</label>
                        <Selector type="artists" v-model="form.features" placeholder="Select featuring artists" max="4" />
                    </div>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Audio & Artwork</h2>
                <div class="space-y-5">
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Audio file</label>
                        <AudioUploader v-model="form.audio" />
                    </div>
                    <div>
                        <label for="youtube-id" class="mb-2 block text-sm font-medium text-nomad-300">YouTube</label>
                        <div class="flex flex-col gap-2 sm:flex-row">
                            <input id="youtube-id" v-model="form.youtube" type="text"
                                class="min-w-0 flex-1 rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                                placeholder="YouTube ID or full URL" />
                            <button type="button" @click="extractYoutubeId"
                                class="rounded-md bg-nomad-800 px-4 py-2 text-white transition-colors hover:bg-nomad-700">
                                Extract ID
                            </button>
                        </div>
                    </div>
                    <YoutubeImage :youtube="form.youtube" v-model="form.cover" v-model:thumbnail="form.meta.thumbnail" />
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Processing Options</h2>
                <div class="space-y-4">
                    <label for="download-youtube-audio" class="flex items-center gap-3 text-nomad-300">
                        <input id="download-youtube-audio" v-model="form.meta.is_youtube" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Download audio from YouTube</span>
                    </label>
                    <label for="retry-audio-process" class="flex items-center gap-3 text-nomad-300">
                        <input id="retry-audio-process" v-model="form.meta.retry" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Retry audio process</span>
                    </label>
                    <label for="smart-music" class="flex items-center gap-3 text-nomad-300">
                        <input id="smart-music" v-model="form.meta.is_smart" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Smart Music</span>
                    </label>
                </div>
            </section>

            <div class="pb-6">
                <button type="submit" :disabled="form.processing"
                    class="rounded-md bg-nomad-accent px-6 py-3 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-60">
                    {{ form.processing ? 'Creating...' : 'Create Song' }}
                </button>
            </div>
        </form>
    </Container>
</template>
<script setup>


import Container from "@/Pages/Backstage/Container.vue";
import Selector from "@/Pages/Backstage/Components/Selector.vue";
import YoutubeImage from "@/Pages/Backstage/Components/YoutubeImage.vue";
import AudioUploader from "@/Pages/Backstage/Components/AudioUploader.vue";
import { Link, useForm } from "@inertiajs/vue3";
const form = useForm({
    name: null,
    artist: null,
    original_artist: null,
    features: null,
    album: null,
    playlists: null,
    released: null,
    youtube: null,
    lyrics: null,
    cover: null,
    audio: null,
    meta: {
        thumbnail: null,
        is_youtube: true,
        cover: false,
        retry: false,
        is_smart: false
    },
});

function extractYoutubeId() {
    if (!form.youtube) return;

    const url = form.youtube.trim();
    let videoId = null;

    // Check for youtu.be format: https://youtu.be/VIDEO_ID
    if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split(/[?&]/)[0];
    }
    // Check for youtube.com format: https://www.youtube.com/watch?v=VIDEO_ID
    else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get('v');
    }
    // If it's already just an ID (no URL), keep it as is
    else if (!url.includes('http') && !url.includes('/')) {
        videoId = url;
    }

    if (videoId) {
        form.youtube = videoId;
    }
}

function submit() {
    form.post('/backstage/songs')
}
</script>
